<script setup>
defineOptions({ name: "SolutionKaliSalaoPage" });
import { defineAsyncComponent, onMounted, onUnmounted, ref } from "vue";
import {
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  DeviceTabletIcon,
  DocumentCheckIcon,
  BanknotesIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  PuzzlePieceIcon,
  BoltIcon,
  ScaleIcon,
} from "@heroicons/vue/24/outline";
import {
  ScissorsIcon,
  FaceSmileIcon,
  HeartIcon,
  SunIcon,
  HandRaisedIcon,
  StarIcon,
  EyeIcon,
  SparklesIcon,
  FireIcon,
} from "@heroicons/vue/24/solid";
import { useSeo } from "@/composables/useSeo";
import FaqPanel from "@/components/ui/FAQ.vue";
import SolutionHero from "@/components/ui/SolutionHero.vue";
import imageBg from "@/assets/solutions/kalisalao.webp";
import visaoGeralImg from "@/assets/visaogeralsalao.webp";
import gestaoTributaria from "@/assets/gestao-tributaria.webp";

const CTA = defineAsyncComponent(() => import("@/components/home/CTA.vue"));
const Clients = defineAsyncComponent(() => import("@/components/home/Clients.vue"));

// Revela o elemento com fade+slide assim que ele entra na viewport ao rolar a página.
const vReveal = {
  mounted(el) {
    el.classList.add("reveal-init");
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("reveal-visible");
          io.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" },
    );
    io.observe(el);
  },
};

const navLinks = [
  { label: "Visão geral", href: "#visao-geral" },
  { label: "Segmentos", href: "#segmentos" },
  { label: "Funcionalidades", href: "#funcionalidades" },
  { label: "Fiscal", href: "#fiscal" },
  { label: "Módulos", href: "#modulos" },
  { label: "Depoimentos", href: "#depoimentos" },
  { label: "Dúvidas", href: "#duvidas" },
];

const activeNav = ref(navLinks[0]?.href ?? "#visao-geral");
const setActiveNav = (href) => {
  activeNav.value = href;
};

const topOffsetPx = 140;
let scrollRaf = null;
const updateActiveFromScroll = () => {
  let current = navLinks[0]?.href ?? "#visao-geral";
  for (const link of navLinks) {
    const sectionEl = document.querySelector(link.href);
    if (!sectionEl) continue;
    const top = sectionEl.getBoundingClientRect().top;
    if (top - topOffsetPx <= 0) {
      current = link.href;
    } else {
      break;
    }
  }
  if (current !== activeNav.value) {
    setActiveNav(current);
  }
};

const onScroll = () => {
  if (scrollRaf) return;
  scrollRaf = requestAnimationFrame(() => {
    scrollRaf = null;
    updateActiveFromScroll();
  });
};

const handleNavClick = (event, href) => {
  event.preventDefault();
  setActiveNav(href);
  history.replaceState(null, "", href);
  const el = document.querySelector(href);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

const syncActiveNavFromHash = () => {
  const hash = window.location.hash;
  if (!hash) return;
  if (navLinks.some((l) => l.href === hash)) {
    setActiveNav(hash);
  }
};

onMounted(() => {
  syncActiveNavFromHash();
  window.addEventListener("hashchange", syncActiveNavFromHash, {
    passive: true,
  });
  window.addEventListener("scroll", onScroll, { passive: true });
  updateActiveFromScroll();
});

onUnmounted(() => {
  window.removeEventListener("hashchange", syncActiveNavFromHash);
  window.removeEventListener("scroll", onScroll);
  if (scrollRaf) cancelAnimationFrame(scrollRaf);
});

function openDemoForSegmento() {
  window.dispatchEvent(
    new CustomEvent("open-demo-modal", { detail: { segmento: "Salão de beleza / Estúdio" } }),
  );
}

// Conteúdo abaixo é baseado no ebook oficial "Kali Gestão Salão" (docs/books/KALI SALAO - EBOOK.pdf).
const segmentos = [
  { icon: ScissorsIcon, label: "Salões de beleza" },
  { icon: FaceSmileIcon, label: "Barbearias" },
  { icon: HeartIcon, label: "Clínicas de estética" },
  { icon: SunIcon, label: "Spas e day spas" },
  { icon: HandRaisedIcon, label: "Manicure e pedicure" },
  { icon: StarIcon, label: "Estúdios de maquiagem" },
  { icon: EyeIcon, label: "Sobrancelhas e cílios" },
  { icon: SparklesIcon, label: "Massoterapia" },
  { icon: FireIcon, label: "Bronzeamento" },
];

const destaques = [
  {
    eyebrow: "Agenda",
    title: "Controle de agenda em qualquer dispositivo",
    text: "Tenha no smartphone, tablet ou notebook os detalhes da agenda e dos clientes, com notificações automáticas de confirmação, cancelamento e reserva.",
  },
  {
    eyebrow: "Agendamento",
    title: "Link de agendamento para o cliente",
    text: "O cliente agenda com qualquer profissional do salão, ou escolhe um específico, tudo em um clique — sem precisar ligar.",
  },
  {
    eyebrow: "Comandas",
    title: "Gestão de comandas por cliente",
    text: "Cada cliente tem sua comanda virtual, com serviços e produtos lançados durante o atendimento e fechamento integrado ao financeiro.",
  },
  {
    eyebrow: "Relacionamento",
    title: "Histórico de clientes e WhatsApp",
    text: "Acesse todos os atendimentos do cliente e use a automação de mensagens via WhatsApp para comunicações personalizadas.",
  },
];

const documentosFiscais = ["NF-e", "NFC-e", "NFS-e", "Livros Fiscais"];

const modulos = [
  {
    icon: BanknotesIcon,
    title: "Gestão financeira",
    subtitle: "Controle total do seu caixa",
    items: [
      "Contas a pagar e receber",
      "Lançamentos recorrentes (aluguel, etc.)",
      "Plano de contas e centro de custo",
      "Abertura e fechamento de caixa",
    ],
  },
  {
    icon: DocumentCheckIcon,
    title: "Gestão fiscal",
    subtitle: "Emissão simples e automatizada",
    items: [
      "NF-e, NFC-e e NFS-e",
      "Carta de correção",
      "Entrada de mercadorias via XML",
      "NFS-e no portal nacional (MEI)",
    ],
  },
  {
    icon: ClipboardDocumentListIcon,
    title: "Pedido de compras",
    subtitle: "Direto com o fornecedor",
    items: [
      "Envio da relação de produtos",
      "Sistema de cotação integrado",
      "Sem acessar catálogo do distribuidor",
    ],
  },
  {
    icon: UserGroupIcon,
    title: "Histórico de clientes",
    subtitle: "Relacionamento que fideliza",
    items: [
      "Todos os atendimentos em um só lugar",
      "Automação de mensagens via WhatsApp",
      "Comunicações personalizadas",
    ],
  },
  {
    icon: ChartBarIcon,
    title: "Gestão inteligente",
    subtitle: "Decisão orientada por dados",
    items: [
      "Relatórios personalizados",
      "Dashboard com gráficos simples",
      "Indicadores do negócio em tempo real",
    ],
  },
  {
    icon: PuzzlePieceIcon,
    title: "Soluções integradas",
    subtitle: "Tudo conectado ao sistema",
    items: [
      "Assinador digital",
      "Boleto bancário integrado",
      "Promoção, combo e lote",
      "Contrato com faturamento recorrente",
    ],
  },
  {
    icon: DevicePhoneMobileIcon,
    title: "SEUPOS",
    subtitle: "App de vendas online e offline",
    items: [
      "Compatível com Android e iOS",
      "Controle o PDV de onde estiver",
    ],
  },
  {
    icon: BoltIcon,
    title: "CONNECT",
    subtitle: "Pagamento sem TEF tradicional",
    items: [
      "Integração Stone, Cielo e Vero",
      "Integração Mercado Pago",
      "Mais ágil e mais barato que o TEF",
    ],
  },
];

useSeo({
  title: "Sistema para Salões de Beleza e Barbearias | Kali Salão",
  description:
    "Agende, atenda e controle comissões com o Kali Salão. Sistema completo para salões, barbearias e clínicas de estética. Teste grátis!",
  image: imageBg,
  schema: {
    "@context": "https://schema.org",
    "@type": ["SoftwareApplication", "LocalBusiness"],
    name: "Kali Salão",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    url: "https://www.kalisistemas.com.br/solucao/kali-salao",
    description:
      "O Kali Salão é um sistema de gestão completo para salões de beleza, barbearias e clínicas de estética. Ele organiza agendamentos, comandas, comissões, estoque, caixa e muito mais — tudo com integração fiscal e acesso online.",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "BRL",
    },
  },
});
</script>

<template>
  <div class="bg-white">
    <SolutionHero
      title="Kali Salão: Sistema de Gestão para Salões de Beleza, Barbearias e Clínicas de Estética"
      description="Agende, atenda, controle comissões, estoque e finanças com um sistema simples, completo e 100% online. Ideal para quem quer mais tempo para encantar seus clientes."
      primary-label="Solicite uma demonstração gratuita"
      primary-href="/contato?type=kali-salao"
      :background-image="imageBg"
      background-class="bg-top bg-cover bg-slate-900"
      overlay-variant="left"
    >
    </SolutionHero>

    <section
      class="sticky top-16 z-40 border-b border-gray-100 shadow-sm backdrop-blur bg-white/95"
    >
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div class="flex gap-6 items-center h-16">
          <nav
            class="flex overflow-x-auto relative gap-6 items-center h-16"
            aria-label="Navegação da página"
          >
            <a
              v-for="item in navLinks"
              :key="item.href"
              :href="item.href"
              class="menu-item-underline relative inline-flex items-center px-2 h-16 text-sm font-bold text-gray-700 hover:text-[#006086] transition-all duration-300 whitespace-nowrap"
              :class="{ 'text-[#006086] active': activeNav === item.href }"
              @click="(e) => handleNavClick(e, item.href)"
            >
              {{ item.label }}
            </a>
          </nav>
        </div>
      </div>
    </section>

    <!-- Visão geral -->
    <section id="visao-geral" class="overflow-hidden relative py-16 bg-white scroll-mt-24 lg:min-h-[620px]">
      <div class="hidden absolute inset-y-0 right-0 w-1/2 lg:block">
        <img
          :src="visaoGeralImg"
          alt="Kali Salão"
          class="object-cover object-center w-full h-full"
        />
      </div>
      <div class="relative px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="lg:max-w-xl">
          <h2
            class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]"
          >
            Feito para o sucesso do seu negócio
          </h2>
          <p class="mt-6 text-lg leading-8 text-gray-600">
            Com as agendas cheias, movimento intenso e muitas tarefas para
            serem feitas, o Kali Salão é a ferramenta ideal para facilitar o
            dia a dia de salões, barbearias, clínicas de estética e negócios
            semelhantes. Agendamento online, rateio automático de comissões,
            controle de estoque e automação de processos, com a praticidade
            de emitir documentos fiscais de forma rápida e simples — gerencie
            seu negócio de qualquer lugar.
          </p>
          <div class="flex gap-8 items-center mt-10 text-[#006086]">
            <DevicePhoneMobileIcon class="w-10 h-10" />
            <ComputerDesktopIcon class="w-10 h-10" />
            <DeviceTabletIcon class="w-10 h-10" />
          </div>
        </div>
      </div>
    </section>

    <!-- Segmentos -->
    <section id="segmentos" class="py-16 bg-gray-100 scroll-mt-24">
      <div class="grid gap-12 items-center px-4 mx-auto max-w-7xl sm:px-6 lg:px-8 lg:grid-cols-[1.3fr_1fr]">
        <div class="grid grid-cols-3 gap-x-6 gap-y-10 sm:grid-cols-4">
          <div
            v-for="s in segmentos"
            :key="s.label"
            v-reveal
            class="flex flex-col items-center text-center group"
          >
            <component
              :is="s.icon"
              class="w-11 h-11 text-kali-blue-600 transition-transform duration-200 ease-out group-hover:-translate-y-1"
            />
            <p
              class="mt-3 text-xs font-semibold text-gray-800 transition-colors duration-200 sm:text-sm group-hover:text-kali-blue-600"
            >
              {{ s.label }}
            </p>
          </div>
        </div>
        <div v-reveal>
          <h2
            class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]"
          >
            Feito para o seu
            <span class="text-kali-blue-600">salão</span>
            ou barbearia
          </h2>
          <p class="mt-4 text-base leading-7 text-gray-600">
            O Kali Salão é a ferramenta ideal para facilitar o dia a dia de
            salões, barbearias, clínicas de estética e negócios semelhantes
            — com agenda, comissões e estoque sob controle.
          </p>
          <button
            type="button"
            @click="openDemoForSegmento"
            class="inline-flex justify-center items-center px-6 py-3 mt-6 text-base font-semibold text-white rounded-full border border-transparent shadow-sm bg-kali-blue-500 hover:bg-kali-blue-600"
          >
            Feito para o seu negócio
          </button>
        </div>
      </div>
    </section>

    <!-- Funcionalidades em destaque -->
    <section id="funcionalidades" class="py-20 bg-gradient-to-br from-white via-gray-50 to-[#e6f0f4] scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2 class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-4">
            Tudo que o seu salão precisa, em um só sistema
          </h2>
          <p class="text-lg leading-8 text-gray-600">
            Da agenda ao caixa, cada funcionalidade foi pensada para tirar
            trabalho manual do seu dia.
          </p>
        </div>
        <div class="grid gap-8 mt-14 sm:grid-cols-2 lg:grid-cols-4">
          <div v-for="d in destaques" :key="d.title" v-reveal>
            <svg viewBox="0 0 400 175" preserveAspectRatio="none" class="object-cover w-full h-44 rounded-2xl">
              <defs>
                <linearGradient :id="`salaoDestaque-${d.eyebrow}`" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stop-color="#e6f0f4" />
                  <stop offset="100%" stop-color="#b8dde8" />
                </linearGradient>
              </defs>
              <rect width="400" height="175" :fill="`url(#salaoDestaque-${d.eyebrow})`" />
            </svg>
            <p class="mt-4 text-xs font-bold tracking-wide text-[#006086] uppercase">
              {{ d.eyebrow }}:
            </p>
            <h3 class="mt-1 text-lg font-bold text-gray-900">{{ d.title }}</h3>
            <p class="mt-2 text-sm leading-relaxed text-gray-600">{{ d.text }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Reforma tributária -->
    <section class="px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div
        v-reveal
        class="overflow-hidden relative bg-kali-blue-600 rounded-[3rem] shadow-xl"
      >
        <div class="grid gap-10 items-center px-8 py-12 lg:grid-cols-2 lg:px-16 lg:py-16">
          <div>
            <h2 class="text-2xl font-black tracking-tight text-white sm:text-3xl lg:text-4xl">
              Reforma tributária: é hora de se preparar!
            </h2>
            <p class="mt-4 text-base leading-7 text-kali-blue-100 sm:text-lg">
              O Kali Salão acompanha as mudanças da reforma tributária em
              tempo real, mantendo sua empresa em conformidade sem parar de
              vender.
            </p>
            <a
              href="/reforma-tributaria"
              class="inline-flex justify-center items-center px-8 py-3 mt-8 text-sm font-bold tracking-wide text-kali-blue-700 uppercase bg-white rounded-full shadow-sm transition hover:bg-kali-blue-50"
            >
              Saiba mais
            </a>
          </div>
          <div class="hidden relative lg:block lg:h-80">
            <img
              :src="gestaoTributaria"
              alt="Especialistas em conformidade fiscal"
              class="object-cover absolute inset-0 w-4/5 h-full rounded-3xl shadow-lg"
            />
            <div
              class="flex absolute -top-6 left-4 justify-center items-center w-16 h-16 rounded-2xl shadow-lg bg-kali-gold"
            >
              <DocumentCheckIcon class="w-8 h-8 text-white" />
            </div>
            <div
              class="flex absolute -bottom-6 right-4 justify-center items-center w-20 h-20 bg-cyan-400 rounded-2xl shadow-lg"
            >
              <ScaleIcon class="w-9 h-9 text-white" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Fiscal -->
    <section id="fiscal" class="py-16 bg-white scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div class="grid gap-12 lg:grid-cols-2">
          <div v-reveal>
            <h2 class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-3">
              Documentos fiscais
            </h2>
            <p class="mb-6 text-lg leading-8 text-gray-600">
              O sistema facilita a emissão de NF-e, NFC-e, carta de correção e
              entrada de mercadorias via XML, com solução integrada e
              automatizada.
            </p>
            <div class="grid grid-cols-2 gap-3">
              <div
                v-for="d in documentosFiscais"
                :key="d"
                class="flex justify-center items-center py-4 text-lg font-bold rounded-lg bg-kali-blue-100 text-kali-blue-700"
              >
                {{ d }}
              </div>
            </div>
          </div>
          <div v-reveal>
            <h2 class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-3">
              Recursos fiscais adicionais
            </h2>
            <p class="mb-6 text-lg leading-8 text-gray-600">
              Para MEIs, o sistema permite a emissão de NFS-e diretamente no
              portal nacional, simplificando a gestão tributária.
            </p>
            <ul class="space-y-2 text-sm text-gray-700">
              <li>
                <span class="font-semibold text-gray-900">Carta de correção —</span>
                corrija dados da nota já emitida sem cancelar o documento.
              </li>
              <li>
                <span class="font-semibold text-gray-900">Entrada de mercadorias via XML —</span>
                importe notas de fornecedores direto no estoque.
              </li>
              <li>
                <span class="font-semibold text-gray-900">NFS-e no portal nacional —</span>
                emissão simplificada para o MEI.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Módulos -->
    <section id="modulos" class="py-16 bg-gradient-to-br from-white via-gray-50 to-[#e6f0f4] scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <h2
          class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-4"
        >
          Módulos do Kali Salão
        </h2>
        <p class="mt-4 mb-12 max-w-2xl text-lg leading-8 text-gray-600">
          Conecte o Kali Salão a módulos adicionais e leve a gestão do seu
          negócio a outro nível.
        </p>
        <div class="grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
          <div v-for="m in modulos" :key="m.title" v-reveal class="text-center">
            <component :is="m.icon" class="mx-auto w-10 h-10 text-[#006086]" />
            <h3 class="mt-4 text-lg font-bold text-gray-900">{{ m.title }}</h3>
            <p class="mt-1 text-sm font-semibold text-gray-700">{{ m.subtitle }}</p>
            <ul class="mt-4 space-y-2 text-sm text-gray-600">
              <li v-for="it in m.items" :key="it">{{ it }}</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Depoimentos -->
    <section id="depoimentos" class="scroll-mt-24">
      <Clients />
    </section>

    <section id="duvidas" class="scroll-mt-24">
      <FaqPanel
        :items="[
          {
            question: 'Tem agenda com confirmação e lembretes?',
            answer: 'Sim. Envio por WhatsApp/e-mail com confirmação automática.',
          },
          {
            question: 'Controla comissões por profissional?',
            answer: 'Sim. Regras de comissão por serviço, profissional e período.',
          },
          {
            question: 'Existe comanda e pacote de serviços?',
            answer:
              'Sim. Comandas, pacotes e recorrência integrados ao financeiro.',
          },
          {
            question: 'Emite NFS-e para serviços?',
            answer: 'Sim. Emissão conforme prefeitura, com integração contábil.',
          },
        ]"
        description="Dúvidas sobre Kali Salão? Veja as respostas e fale com nosso time."
        cta-label="Falar com Consultor"
        :cta-opens-modal="true"
      />
    </section>

    <section id="fale-conosco" class="scroll-mt-16">
      <CTA />
    </section>
  </div>
</template>

<style scoped>
.reveal-init {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;
}

.reveal-visible {
  opacity: 1;
  transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
  .reveal-init {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>
